import * as THREE from "three";
import { TransformType } from "utils/interface.utils";
import { updateObjectInTargetImage, setIsUpdatingFromEditorWorkspace } from "store/reducers/editorSlice";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { applyTransformCursor } from "components/editor_workspace/scene/transform_cursor.helper";

export const dragObjectsAndUpdateRotation = (workspace: WorkspaceContext) => {
    const { rendererRef, canvasRef, sceneRef, cameraRef, objectsRef, selectedTransformControlRef } = workspace.refs;
    let isRotating = false;
    let rotationStartAngle = 0;
    let rotationStartMousePos = new THREE.Vector2();
    let rotatingObject: THREE.Object3D | null = null;
    let rotationCorner: string | null = null;
    let initialRotation: number = 0;
    let lastRotationDegrees: number | null = null;

    const onMouseDown = (event: MouseEvent) => {
        if (!sceneRef.current || !cameraRef.current || !rendererRef.current || selectedTransformControlRef.current !== TransformType.ROTATION) return;
        

        const mouse = new THREE.Vector2();
        const rect = rendererRef.current.domElement.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        
        const mouseScreenPos = new THREE.Vector2(
            event.clientX - rect.left,
            event.clientY - rect.top
        );
        
        let targetObject: THREE.Object3D | null = null;
        let detectedCorner: string | null = null;
        
        // Check all objects for corner detection
        for (const [objectId, objectData] of Array.from(objectsRef.current.entries())) {
            const object = objectData.mesh;
            if (object && object.userData.transformBox && object.userData.isDraggable) {
                const transformBox = object.userData.transformBox;
                if (transformBox.userData.detectCorner) {
                    const corner = transformBox.userData.detectCorner(mouseScreenPos);
                    if (corner) {
                        targetObject = object;
                        detectedCorner = corner;
                        break; // Use the first corner detected
                    }
                }
            }
        }

        // If we found a corner, start rotation
        if (targetObject && detectedCorner) {
            isRotating = true;
            rotatingObject = targetObject;
            rotationCorner = detectedCorner;
            rotationStartMousePos.copy(mouseScreenPos);
            
            // Store the initial rotation for relative calculations
            initialRotation = rotatingObject.rotation.z;
            
            // Calculate initial angle from object center to mouse
            const objectCenter = getObjectScreenCenter(targetObject);
            const initialVector = mouseScreenPos.clone().sub(objectCenter);
            rotationStartAngle = Math.atan2(initialVector.y, initialVector.x);
            
            // Update cursor for rotation
            applyTransformCursor(canvasRef.current, { tool: TransformType.ROTATION, corner: rotationCorner, dragging: true });
            return;
        }
    };
    
    const onMouseMove = (event: MouseEvent) => {
        if (!rendererRef.current) return;
        
        const rect = rendererRef.current.domElement.getBoundingClientRect();
        const mouseScreenPos = new THREE.Vector2(
            event.clientX - rect.left,
            event.clientY - rect.top
        );

        if (isRotating && rotatingObject) {
            handleRotation(mouseScreenPos);
        } else {
            // Handle cursor updates for corner detection
            handleCursorUpdate();
        }
    };
    
    // Mouse up handler
    const onMouseUp = () => {
        if (isRotating) {
            // Final frame of the gesture — commits the whole rotation as a single undo step.
            if (rotatingObject && lastRotationDegrees !== null) {
                updateObjectRotationInStore(workspace, rotatingObject.userData.id, lastRotationDegrees, true);
            }
            isRotating = false;
            rotatingObject = null;
            rotationCorner = null;
            lastRotationDegrees = null;
            applyTransformCursor(canvasRef.current, { tool: selectedTransformControlRef.current, corner: null, dragging: false });
        }
    };

    const getObjectScreenCenter = (object: THREE.Object3D) => {
        if (!cameraRef.current || !rendererRef.current) return new THREE.Vector2();
        
        const worldPosition = new THREE.Vector3();
        object.getWorldPosition(worldPosition);
        
        const screenPosition = worldPosition.clone().project(cameraRef.current);

        return new THREE.Vector2(
            (screenPosition.x + 1) * rendererRef.current.domElement.clientWidth / 2,
            (-screenPosition.y + 1) * rendererRef.current.domElement.clientHeight / 2
        );
    };
    
    // Handle rotation logic
    const handleRotation = (currentMousePos: THREE.Vector2) => {
        if (!rotatingObject || !cameraRef.current || !rendererRef.current) return;
        
        const objectCenter = getObjectScreenCenter(rotatingObject);
        
        // Calculate current angle from object center to mouse
        const currentVector = currentMousePos.clone().sub(objectCenter);
        const currentAngle = Math.atan2(currentVector.y, currentVector.x);
        
        // Calculate rotation delta
        let deltaAngle = currentAngle - rotationStartAngle;
        deltaAngle = -deltaAngle;
        
        // Apply corner-specific rotation behavior
        switch (rotationCorner) {
            case 'topRight':
                // For top-right corner, dragging down rotates clockwise
                break;
            case 'topLeft':
                // For top-left corner, reverse the rotation direction
                break;
            case 'bottomRight':
                // For bottom-right corner, reverse the rotation direction  
                break;
            case 'bottomLeft':
                // For bottom-left corner, normal rotation
                break;
        }

        // Apply rotation (assuming Z-axis rotation for 2D-like objects)
        const newRotation = initialRotation + deltaAngle;
        rotatingObject.rotation.z = newRotation;
        
        // Update transform box rotation if it exists
        if (rotatingObject.userData.transformBox) {
            rotatingObject.userData.transformBox.rotation.z = newRotation;
        }
        
        // Render the scene
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
        
        // Update the object properties (if you need to sync with your state management)
        const rotationDegrees = THREE.MathUtils.radToDeg(newRotation);
        lastRotationDegrees = rotationDegrees;
        updateObjectRotationInStore(workspace, rotatingObject.userData.id, rotationDegrees, false);
    };
    
    // The rotate cursor shows across the canvas while the rotation tool is the active transform
    // control. For any other tool this must not write: Position and Scale own the cursor then.
    const handleCursorUpdate = () => {
         if (selectedTransformControlRef.current !== TransformType.ROTATION) return;
         applyTransformCursor(canvasRef.current, { tool: TransformType.ROTATION, corner: null, dragging: false });
    };

    if (canvasRef.current && sceneRef.current) {
        canvasRef.current.addEventListener('mousedown', onMouseDown);
        canvasRef.current.addEventListener('mousemove', onMouseMove);
        canvasRef.current.addEventListener('mouseup', onMouseUp);
        sceneRef.current.traverse((child) => {
            if (child.userData.isTransformBox) {
                child.addEventListener('mouseleave', onMouseUp);
            }
        });
    }
    
    return {
        cleanup: () => {
            if (canvasRef.current) {
                canvasRef.current.removeEventListener('mousedown', onMouseDown);
                canvasRef.current.removeEventListener('mousemove', onMouseMove);
                canvasRef.current.removeEventListener('mouseup', onMouseUp);
            }
            if (sceneRef.current) {
                sceneRef.current.traverse((child) => {
                    if (child.userData.isTransformBox) {
                        child.removeEventListener('mouseleave', onMouseUp);
                    }
                });
            }
        },

        isObjectBeingDraggedAndRotated: (objectId: string) => isRotating && rotatingObject?.userData.id === objectId
    }
}   

export const updateObjectRotationInStore = (workspace: WorkspaceContext, objectID: string, rotation: number, commitHistory: boolean = false) => {
    const { targetImageDataListRef, selectedTargetIndexRef } = workspace.refs;
    const { dispatch } = workspace;
    const targetImageId = targetImageDataListRef.current[selectedTargetIndexRef.current ?? 0]?.id;

    if (!targetImageId || !objectID) return;

    // Find the object in the current state
    const currentObject = targetImageDataListRef.current[selectedTargetIndexRef.current ?? 0]?.objects.find(
        (obj) => obj.id === objectID
    );

    if (!currentObject) return;

    const updatedObject = {
        ...currentObject,
        properties: {
            ...currentObject.properties,
            rotationX: Math.round(rotation)
        }
    };
    // Mid-drag frames pass commitHistory:false; the final drag-end frame passes true so the
    // whole gesture collapses into a single undo step.
    dispatch(updateObjectInTargetImage({
        targetImageID: targetImageId,
        object: updatedObject,
        commitHistory
    }));
    dispatch(setIsUpdatingFromEditorWorkspace(true));
};
